import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationList } from '@/components/notifications';
import type { NotificationRecord } from '@/notifications';
import { useNotifications } from '@/notifications';

type NotificationCenterTab = 'all' | 'unread' | 'system' | 'security';

export function NotificationCenterPage() {
  const { t } = useTranslation('entity-portal');
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    setFilters,
    refresh,
    markAllRead,
    markRead,
    markUnread,
    archive,
    remove,
  } = useNotifications();

  const [activeTab, setActiveTab] = useState<NotificationCenterTab>('all');
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationRecord | null>(null);

  const selectedDetail = useMemo(
    () =>
      notifications.find(
        (notification) => notification.id === selectedNotification?.id,
      ) ?? selectedNotification,
    [notifications, selectedNotification],
  );

  const handleTabChange = (
    _event: SyntheticEvent,
    value: NotificationCenterTab,
  ) => {
    setActiveTab(value);

    if (value === 'all') {
      setFilters({ category: 'all', status: 'all' });
      return;
    }

    if (value === 'unread') {
      setFilters({ status: 'unread', category: 'all' });
      return;
    }

    if (value === 'system') {
      setFilters({ category: 'system', status: 'all' });
      return;
    }

    setFilters({ category: 'security', status: 'all' });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: 'background.default',
        minHeight: '100%',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          overflow: 'hidden',
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 1.25 }}>
          <Typography variant="caption" color="text.secondary">
            {t('notificationCenter.breadcrumbPortal')} &gt;{' '}
            {t('notificationCenter.breadcrumbLauncher')} &gt;{' '}
            {t('notificationCenter.breadcrumbCenter')}
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ mt: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h2" component="h1">
                {t('notificationCenter.title')}
              </Typography>
              <Chip
                label={t('notificationCenter.unreadPill', {
                  count: unreadCount,
                })}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="text"
                onClick={() => {
                  void markAllRead();
                }}
                sx={{
                  color: 'primary.main',
                  textTransform: 'none',
                }}
              >
                {t('notificationCenter.markAllAsRead')}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  // Placeholder for settings drawer/page.
                }}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                }}
              >
                {t('notificationCenter.settings')}
              </Button>
              <Button
                variant="text"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => {
                  void refresh();
                }}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                }}
              >
                {t('notificationCenter.refresh')}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 1, md: 2 },
            minHeight: 42,
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 2,
            },
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
            },
            '& .Mui-selected': {
              color: 'primary.main !important',
            },
          }}
        >
          <Tab
            value="all"
            label={t('notificationCenter.tabAllNotifications')}
          />
          <Tab value="unread" label={t('notificationCenter.tabUnreadOnly')} />
          <Tab value="system" label={t('notificationCenter.tabSystemAlerts')} />
          <Tab
            value="security"
            label={t('notificationCenter.tabSecurityEvents')}
          />
        </Tabs>

        <Divider />

        <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5 }}>
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            onRefresh={() => {
              void refresh();
            }}
            onMarkRead={(id) => {
              void markRead(id);
            }}
            onMarkUnread={(id) => {
              void markUnread(id);
            }}
            onArchive={(id) => {
              void archive(id);
            }}
            onDelete={(id) => {
              void remove(id);
            }}
            onOpenRelatedProduct={(notification) => {
              if (notification.deepLink) {
                navigate(notification.deepLink);
                return;
              }

              if (notification.actionUrl) {
                window.open(
                  notification.actionUrl,
                  '_blank',
                  'noopener,noreferrer',
                );
              }
            }}
            onViewDetails={(notification) => {
              setSelectedNotification(notification);
            }}
          />

          <Paper
            variant="outlined"
            sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: 'background.default',
              borderColor: 'divider',
            }}
          >
            <Typography variant="tableHeader" color="text.secondary">
              {t('notificationCenter.detailsFutureLabel')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedDetail
                ? `${selectedDetail.productName}: ${selectedDetail.title}`
                : t('notificationCenter.detailsPlaceholder')}
            </Typography>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
